import { useRef, useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Badge,
  Box,
  ClickAwayListener,
  List,
  ListItemButton, //h
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Popper,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  Chip,
  Button
} from '@mui/material';

// project-imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { ThemeMode } from 'config';
import axiosServices from 'utils/axios';

// assets
import { Notification, Add, Box as BoxIcon, User, Wallet } from 'iconsax-react';
import Avatar from 'components/@extended/Avatar';

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',

  transform: 'none'
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

const NotificationPage = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

  const anchorRef = useRef(null);
  const [read, setRead] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [displayCount, setDisplayCount] = useState(20);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await axiosServices.get('admin/notifications/list');
        if (response.data?.result?.notifications) {
          setNotifications(response.data.result.notifications);
          setRead(response.data.result.unreadCount ?? 0);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMarkAsRead = async (id) => {
    try {
      await axiosServices.patch('admin/notifications/mark-as-read', { id });
      setNotifications((prev) => 
        prev.map((n) => n.id === id ? { ...n, is_readed: 1 } : n)
      );
      setRead((prev) => prev > 0 ? prev - 1 : 0);
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const handleLoadMore = () => {
    setDisplayCount((prev) => prev + 15);
  };

  const displayedNotifications = notifications.slice(0, displayCount);
  const hasMore = notifications.length > displayCount;

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'secondary.200' : 'secondary.200';
  const iconBackColor = theme.palette.mode === ThemeMode.DARK ? 'background.default' : 'secondary.100';

  return (
    <Box sx={{ flexShrink: 0, ml: 0.5 }}>
      <IconButton
        color="secondary"
        variant="light"
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        size="large"
        sx={{ color: 'secondary.main', bgcolor: open ? iconBackColorOpen : iconBackColor, p: 1 }}
      >
        <Badge badgeContent={read > 99 ? '99+' : read} color="error">
          <Notification variant="Bold" />
        </Badge>
      </IconButton>
      <Popper
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [matchesXs ? -5 : 0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={matchesXs ? 'top' : 'top-right'} sx={{ overflow: 'hidden' }} in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                borderRadius: 1.5,
                width: '100%',
                minWidth: 320,
                maxWidth: 450,
                [theme.breakpoints.down('md')]: {
                  maxWidth: 320
                },
                bgcolor: 'background.paper',
                overflow: 'hidden'
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard 
                  elevation={0} 
                  border={false} 
                  content={false}
                  sx={{ 
                    bgcolor: 'background.paper',
                    '& .MuiCardContent-root': {
                      bgcolor: 'background.paper'
                    }
                  }}
                >
                  {/* Header */}
                  <Stack 
                    direction="row" 
                    alignItems="center" 
                    justifyContent="space-between" 
                    sx={{ 
                      p: 2, 
                      pb: 1.5,
                      bgcolor: 'background.paper',
                      borderBottom: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Typography variant="h5">Notifications</Typography>
                      {read > 0 && (
                        <Chip 
                          label={`${read} new`} 
                          color="success" 
                          size="small"
                          sx={{ 
                            height: 24,
                            fontSize: '0.75rem',
                            fontWeight: 600
                          }}
                        />
                      )}
                    </Stack>
                    <IconButton
                      size="small"
                      onClick={() => setOpen(false)}
                      sx={{
                        color: 'text.secondary',
                        '&:hover': { 
                          bgcolor: 'error.lighter',
                          color: 'error.main' 
                        }
                      }}
                    >
                      <Add style={{ transform: 'rotate(45deg)' }} size={20} />
                    </IconButton>
                  </Stack>

                  {/* Notifications List */}
                  <List
                    component="nav"
                    sx={{
                      maxHeight: 420,
                      overflowY: 'auto',
                      p: 2,
                      pt: 1,
                      pb: 0,
                      bgcolor: 'background.paper',
                      '& .MuiListItemButton-root': {
                        p: 1.5,
                        mb: 1,
                        borderRadius: 1.5,
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: 'background.default',
                        '&:hover': {
                          bgcolor: 'action.hover',
                          borderColor: theme.palette.primary.light,
                          transform: 'translateY(-2px)',
                          boxShadow: theme.shadows[2]
                        },
                        transition: 'all 0.2s',
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                    {loading ? (
                      [1, 2, 3].map((item) => (
                        <ListItemButton key={item} sx={{ mb: 1 }}>
                          <ListItemAvatar>
                            <Skeleton variant="circular" width={40} height={40} />
                          </ListItemAvatar>
                          <ListItemText
                            primary={<Skeleton variant="text" width="60%" />}
                            secondary={<Skeleton variant="text" width="40%" />}
                          />
                        </ListItemButton>
                      ))
                    ) : displayedNotifications.length > 0 ? (
                      displayedNotifications.map((notif, index) => (
                        <ListItemButton 
                          key={index} 
                          onClick={() => Number(notif.is_readed) === 0 && handleMarkAsRead(notif.id)}
                          sx={{ 
                            bgcolor: Number(notif.is_readed) === 0 ? 'primary.lighter' : 'background.default',
                            position: 'relative',
                            '&:hover': {
                              bgcolor: Number(notif.is_readed) === 0 ? 'primary.lighter' : 'action.hover'
                            }
                          }}
                        >
                          {/* Green dot indicator for unread */}
                          {Number(notif.is_readed) === 0 && (
                            <Box
                              sx={{
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'success.main'
                              }}
                            />
                          )}
                          <ListItemAvatar sx={{ ml: Number(notif.is_readed) === 0 ? 1.5 : 0 }}>
                            <Avatar 
                              type="filled" 
                              color={Number(notif.is_readed) === 0 ? 'primary' : 'secondary'}
                              sx={{ width: 44, height: 44 }}
                            >
                              {Number(notif.type) === 1 && <BoxIcon size={22} variant="Bold" />}
                              {Number(notif.type) === 2 && <BoxIcon size={22} variant="Bold" />}
                              {Number(notif.type) === 3 && <Wallet size={22} variant="Bold" />}
                              {Number(notif.type) === 4 && <User size={22} variant="Bold" />}
                              {![1, 2, 3, 4].includes(Number(notif.type)) && <Notification size={22} variant="Bold" />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                  fontWeight: Number(notif.is_readed) === 0 ? 600 : 400,
                                  mb: 0.25,
                                  color: 'text.primary'
                                }}
                              >
                                {notif.title || 'Notification'}
                              </Typography>
                            }
                            secondary={
                              <Typography 
                                variant="caption" 
                                color="text.secondary"
                                sx={{ 
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  lineHeight: 1.4
                                }}
                              >
                                {notif.message || notif.body}
                              </Typography>
                            }
                          />
                          {notif.createdAt && (
                            <ListItemSecondaryAction sx={{ top: 12, right: 12 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true })
                                  .replace('about ', '')
                                  .replace('less than a minute ago', 'just now')
                                  .replace(' minutes', 'm')
                                  .replace(' minute', 'm')
                                  .replace(' hours', 'h')
                                  .replace(' hour', 'h')
                                  .replace(' days', 'd')
                                  .replace(' day', 'd')
                                  .replace(' ago', ' ago')}
                              </Typography>
                            </ListItemSecondaryAction>
                          )}
                        </ListItemButton>
                      ))
                    ) : (
                      <Stack alignItems="center" justifyContent="center" sx={{ py: 5, textAlign: 'center' }}>
                        <Notification size={48} variant="Bulk" color={theme.palette.text.secondary} />
                        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                          No notifications
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          You&apos;re all caught up!
                        </Typography>
                      </Stack>
                    )}

                    {/* Load More Button */}
                    {!loading && hasMore && (
                      <Box sx={{ 
                        textAlign: 'center', 
                        pt: 2, 
                        pb: 2,
                        bgcolor: 'background.paper',
                        borderTop: `1px solid ${theme.palette.divider}`,
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 1
                      }}>
                        <Button
                          variant="outlined"
                          onClick={handleLoadMore}
                          fullWidth
                          sx={{
                            color: 'primary.main',
                            borderColor: 'primary.main',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            textTransform: 'none',
                            mx: 2,
                            width: 'calc(100% - 32px)',
                            '&:hover': {
                              bgcolor: 'primary.lighter',
                              borderColor: 'primary.dark'
                            }
                          }}
                        >
                          Load More ({notifications.length - displayCount} more)
                        </Button>
                      </Box>
                    )}
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default NotificationPage;
